import { useEffect, useCallback, useRef } from 'react';

/**
 * Smart sticky sidebar hook - e-commerce style dual-direction sticky behavior.
 *
 * Uses direct DOM manipulation for smooth real-time scrolling.
 * Creates a placeholder to maintain layout when sidebar becomes fixed.
 *
 * @param topOffset - Distance from viewport top when stuck to top (default: 96px for nav)
 * @param bottomOffset - Distance from viewport bottom when stuck to bottom (default: 16px)
 * @returns callbackRef - Attach to the sidebar element
 */
export function useSmartSticky(
  topOffset: number = 96,
  bottomOffset: number = 16
): (node: HTMLElement | null) => void {
  const sidebarRef = useRef<HTMLElement | null>(null);
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const initialTopRef = useRef<number | null>(null);
  const isFixedRef = useRef<boolean>(false);

  // Callback ref - called when element mounts/unmounts
  const callbackRef = useCallback((node: HTMLElement | null) => {
    // Cleanup old placeholder if element changes
    if (placeholderRef.current && placeholderRef.current.parentNode) {
      placeholderRef.current.parentNode.removeChild(placeholderRef.current);
      placeholderRef.current = null;
    }

    sidebarRef.current = node;

    if (node) {
      // Store the initial position (distance from document top)
      const rect = node.getBoundingClientRect();
      initialTopRef.current = rect.top + window.scrollY;

      // Create placeholder element to maintain layout space
      const placeholder = document.createElement('div');
      placeholder.style.display = 'none';
      placeholder.style.width = `${node.offsetWidth}px`;
      placeholder.style.height = `${node.offsetHeight}px`;
      node.parentNode?.insertBefore(placeholder, node);
      placeholderRef.current = placeholder;
    }
  }, []);

  useEffect(() => {
    const updatePosition = () => {
      const sidebar = sidebarRef.current;
      const placeholder = placeholderRef.current;
      const initialTop = initialTopRef.current;

      if (!sidebar || initialTop === null) {
        return;
      }

      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const sidebarHeight = sidebar.offsetHeight;
      const sidebarWidth = sidebar.offsetWidth;
      const availableHeight = viewportHeight - topOffset - bottomOffset;

      // If sidebar fits in available viewport space, use simple sticky
      if (sidebarHeight <= availableHeight) {
        if (isFixedRef.current) {
          // Reset to sticky
          sidebar.style.position = 'sticky';
          sidebar.style.top = `${topOffset}px`;
          sidebar.style.width = '';
          if (placeholder) placeholder.style.display = 'none';
          isFixedRef.current = false;
        } else if (sidebar.style.position !== 'sticky') {
          sidebar.style.position = 'sticky';
          sidebar.style.top = `${topOffset}px`;
        }
        return;
      }

      // Sidebar is taller than viewport - need smart positioning
      // Calculate where sidebar would naturally be if it just scrolled with page
      const naturalTop = initialTop - scrollY;

      // Determine the display position with constraints
      let displayTop: number;
      let shouldBeFixed = true;

      if (naturalTop >= topOffset) {
        // Sidebar's natural position is at or below the nav
        // Keep it in normal flow (not fixed)
        shouldBeFixed = false;
        displayTop = topOffset;
      } else if (naturalTop + sidebarHeight <= viewportHeight - bottomOffset) {
        // Sidebar's bottom would be at or above viewport bottom
        // Pin to viewport bottom
        displayTop = viewportHeight - sidebarHeight - bottomOffset;
      } else {
        // In transition zone - sidebar moves with scroll at 1:1
        displayTop = naturalTop;
      }

      if (shouldBeFixed) {
        if (!isFixedRef.current) {
          // Switching to fixed - show placeholder
          if (placeholder) {
            placeholder.style.display = 'block';
            placeholder.style.width = `${sidebarWidth}px`;
            placeholder.style.height = `${sidebarHeight}px`;
          }
          isFixedRef.current = true;
        }
        sidebar.style.position = 'fixed';
        sidebar.style.top = `${displayTop}px`;
        sidebar.style.width = `${sidebarWidth}px`;
      } else {
        if (isFixedRef.current) {
          // Switching back to static - hide placeholder
          if (placeholder) placeholder.style.display = 'none';
          isFixedRef.current = false;
        }
        sidebar.style.position = 'relative';
        sidebar.style.top = '';
        sidebar.style.width = '';
      }
    };

    // Direct scroll handler - no requestAnimationFrame for smoother feel
    const handleScroll = () => {
      updatePosition();
    };

    const handleResize = () => {
      const sidebar = sidebarRef.current;
      if (sidebar && placeholderRef.current) {
        // Reset position briefly to get true measurements
        const wasFixed = isFixedRef.current;
        if (wasFixed) {
          sidebar.style.position = 'relative';
          sidebar.style.top = '';
          sidebar.style.width = '';
          placeholderRef.current.style.display = 'none';
        }

        // Update initial position
        const rect = sidebar.getBoundingClientRect();
        initialTopRef.current = rect.top + window.scrollY;

        // Update placeholder size
        placeholderRef.current.style.width = `${sidebar.offsetWidth}px`;
        placeholderRef.current.style.height = `${sidebar.offsetHeight}px`;

        isFixedRef.current = false;
        updatePosition();
      }
    };

    // Watch for sidebar content changes (e.g., tag tree expands)
    const resizeObserver = new ResizeObserver(() => {
      if (placeholderRef.current && sidebarRef.current) {
        placeholderRef.current.style.height = `${sidebarRef.current.offsetHeight}px`;
      }
      updatePosition();
    });

    if (sidebarRef.current) {
      resizeObserver.observe(sidebarRef.current);
    }

    // Initial calculation
    updatePosition();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();

      // Cleanup placeholder on unmount
      if (placeholderRef.current && placeholderRef.current.parentNode) {
        placeholderRef.current.parentNode.removeChild(placeholderRef.current);
        placeholderRef.current = null;
      }
    };
  }, [topOffset, bottomOffset]);

  return callbackRef;
}

export default useSmartSticky;
